import { serializeProject, parseProjectData } from './project/serializer';
import { importProjectFromBuffer } from './project/importer';
import { exportProjectToFormat } from './project/exporter';
import { SerializedProject } from './project/types';
import { DecodedImage } from './format-interface';

/**
 * @file project-io.ts
 * @description Main facade for HCIE project serialization and format conversion.
 * Following AGENTS.md, this module provides stateless "flat functions" for all IO operations.
 */

/**
 * @function saveProjectToDataString
 * @description Serializes the provided project state and layers into a JSON string.
 * @param {number} width - Image width.
 * @param {number} height - Image height.
 * @param {string} backgroundColor - Project background color.
 * @param {any[]} layers - Array of layer objects.
 * @returns {Promise<string>} Serialized project JSON string.
 */
export async function saveProjectToDataString(
  width: number,
  height: number,
  backgroundColor: string,
  layers: any[]
): Promise<string> {
  return serializeProject(width, height, backgroundColor, layers);
}

/**
 * @function parseProjectDataString
 * @description Parses an HCIE project JSON string into a structured data object.
 * @param {string} jsonString - The input JSON data.
 * @returns {SerializedProject} The parsed project structure.
 */
export function parseProjectDataString(jsonString: string): SerializedProject {
  return parseProjectData(jsonString);
}

/**
 * @function importProjectBuffer
 * @description Imports binary image or project data from an ArrayBuffer.
 * @param {ArrayBuffer} buffer - The input binary data.
 * @param {string} fileName - Original file name for extension detection.
 * @returns {Promise<SerializedProject | DecodedImage>} Decoded data structure.
 */
export async function importProjectBuffer(
  buffer: ArrayBuffer,
  fileName: string
): Promise<SerializedProject | DecodedImage> {
  return importProjectFromBuffer(buffer, fileName);
}

/**
 * @function exportProjectBuffer
 * @description Exports the project to a binary buffer in the specified format.
 * @param {number} width - Image width.
 * @param {number} height - Image height.
 * @param {any[]} layers - Array of layers.
 * @param {string} formatExtension - Target format (e.g., '.png').
 * @returns {Promise<Uint8Array | string>} Encoded binary data or JSON string.
 */
export async function exportProjectBuffer(
  width: number,
  height: number,
  layers: any[],
  formatExtension: string
): Promise<Uint8Array | string> {
  return exportProjectToFormat(width, height, layers, formatExtension);
}

